import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import DateInput from '../components/DateInput';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL || '';

interface PreviousClaim {
  id?: string;
  description: string;
  policyNumber: string;
  previousInsurer: string;
  claimDate: string;
  claimValue: string;
}

const AddPreviousClaimPage = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddPreviousClaimPage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode 
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<PreviousClaim>({
    description: '',
    policyNumber: '',
    previousInsurer: '',
    claimDate: '',
    claimValue: ''
  });
  
  const [errors, setErrors] = useState({
    description: '',
    policyNumber: '',
    previousInsurer: '',
    claimDate: '',
    claimValue: '',
    server: ''
  });

  const [saveMessage, setSaveMessage] = useState('');

  // Format currency for display
  const formatCurrency = (value: string): string => {
    if (!value) return '';
    const numValue = parseFloat(value.replace(/,/g, ''));
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Parse currency for storage
  const parseCurrency = (value: string): string => {
    if (!value) return '';
    return value.replace(/[^0-9.]/g, '');
  };

  // Fetch existing claim if editing
  useEffect(() => {
    const editId = new URLSearchParams(location.search).get('editId');
    if (editId && quoteId) {
      setEditingId(editId);
      fetchClaimForEdit(editId);
    }
  }, [quoteId, location.search]);

  const fetchClaimForEdit = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/clients-personal-claims/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const claim = response.data.data || response.data;
      setFormData({
        description: claim.description || '',
        policyNumber: claim.policy_number || claim.policyNumber || '',
        previousInsurer: claim.previous_insurer || claim.previousInsurer || '',
        claimDate: claim.claim_date || claim.claimDate || '',
        claimValue: claim.claim_value || claim.claimValue || ''
      });
    } catch (error) {
      console.error('Error fetching claim for edit:', error);
    }
  };

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'claimValue') {
      // Format as currency while typing
      const cleaned = value.replace(/[^0-9.]/g, '');
      const parts = cleaned.split('.');
      let formatted = parts[0];
      if (parts.length > 1 && parts[1].length > 2) {
        formatted = `${parts[0]}.${parts[1].slice(0, 2)}`;
      } else {
        formatted = cleaned;
      }
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    setErrors(prev => ({ ...prev, [name]: '', server: '' }));
  };

  const handleClaimValueBlur = () => {
    if (formData.claimValue) {
      const formatted = formatCurrency(formData.claimValue);
      setFormData(prev => ({ ...prev, claimValue: formatted }));
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors = { ...errors };
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }
    if (!formData.policyNumber.trim()) {
      newErrors.policyNumber = 'Policy Number is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Validate clientId
    if (!clientId || isNaN(parseInt(clientId))) {
      setErrors(prev => ({ ...prev, server: 'Valid client ID is required' }));
      return;
    }
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      // Clean the quoteId - convert "null" string or invalid values to actual null
let cleanQuoteId: number | null = null;
if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
  cleanQuoteId = parseInt(quoteId);
}
      
      // Parse claim value for storage
      const claimValueNum = formData.claimValue 
        ? parseFloat(parseCurrency(formData.claimValue)) 
        : null;
      
      const claimData = {
        description: formData.description,
        policy_number: formData.policyNumber,
        previous_insurer: formData.previousInsurer || null,
        claim_date: formData.claimDate || new Date().toISOString().split('T')[0],
        claim_value: claimValueNum,
        quote_id: cleanQuoteId,
        client_id: parseInt(clientId)
      };
      
      // If no quote_id, save to localStorage (draft mode)
      if (!cleanQuoteId) {
        console.log('💾 No quote ID - saving previous claim to localStorage as draft');
        
        const storageKey = `client_${clientId}_previous_claims_draft`;
        const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
        existingDrafts.push({
          ...claimData,
          id: `temp-${Date.now()}`,
          isTemp: true
        });
        localStorage.setItem(storageKey, JSON.stringify(existingDrafts));
        
        setSaveMessage('Previous claim saved to draft! It will be saved when you create the quote.');

setTimeout(() => {
  if (isMasterQuoteMode && returnTo) {
    // Should not happen for draft mode, but handle gracefully
    const decodedReturnUrl = decodeURIComponent(returnTo);
    navigate(decodedReturnUrl);
  } else {
    navigate(`/create-quote/${clientId}`, { 
      state: { activeTab: 'quotes', refreshProducts: true, hasDrafts: true }
    });
  }
}, 1500);
      } else {
  // Has quote_id - save to database
  if (editingId) {
    await axios.put(
      `${API_URL}/api/clients-personal-claims/${editingId}`,
      claimData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } else {
    await axios.post(
      `${API_URL}/api/clients-personal-claims`,
      claimData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }
  
  // ✅ FIX: Navigate based on mode
  setTimeout(() => {
    const validClientId = clientId || new URLSearchParams(location.search).get('clientId');
    
    if (!validClientId) {
      console.error('❌ No clientId available for navigation');
      setErrors(prev => ({ ...prev, server: 'Error: Missing client ID' }));
      setIsLoading(false);
      return;
    }
    
    if (isMasterQuoteMode && returnTo) {
      // Master quote mode - navigate back to master quote page
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=previous_claim&clientId=${validClientId}`;
      console.log('📍 Returning to master quote page:', finalUrl);
      navigate(finalUrl);
    } else if (cleanQuoteId) {
      // Create quote mode but with quoteId - navigate to create quote page
      navigate(`/create-quote/${validClientId}`, { 
        state: { refreshProducts: true, activeTab: 'quotes' } 
      });
    } else {
      // Fallback
      navigate(`/create-quote/${validClientId}`, { 
        state: { refreshProducts: true, activeTab: 'quotes' } 
      });
    }
  }, 1500);
}
      
    } catch (error: any) {
      setErrors(prev => ({
        ...prev,
        server: error.response?.data?.message || `Failed to ${editingId ? 'update' : 'add'} previous claim`
      }));
    } finally {
      setIsLoading(false);
    }
  };

    if (!can('quotes', 'write')) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to add Previous Claims.</p>
      </div>
    );
  }


  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Previous Claim' : 'Add Previous Claim'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">

            <button
              onClick={() => {
                if (isMasterQuoteMode && returnTo) {
                  // Go back to master quote page with refresh flag
                  const decodedReturnUrl = decodeURIComponent(returnTo);
                  const separator = decodedReturnUrl.includes('?') ? '&' : '?';
                  const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=previous_claim&clientId=${clientId}`;
                  console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
                  navigate(finalUrl);
                } else {
                  // Go back to create quote page
                  navigate(`/create-quote/${clientId}`);
                }
              }}
              className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              ← Back to Quote
            </button>
            
            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-800 mb-6">
                {editingId ? 'Edit Previous Claim' : 'Add Previous Claim'}
              </h3>
              
              {errors.server && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {errors.server}
                </div>
              )}

              {saveMessage && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                  {saveMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Description */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-4 py-3 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Describe the previous claim..."
                  />
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>
                
                {/* Policy Number and Previous Insurer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Policy Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="policyNumber"
                      value={formData.policyNumber}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border ${errors.policyNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Policy number"
                    />
                    {errors.policyNumber && <p className="text-red-500 text-sm mt-1">{errors.policyNumber}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Previous Insurer</label>
                    <input
                      type="text"
                      name="previousInsurer"
                      value={formData.previousInsurer}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Insurance company"
                    />
                  </div>
                </div>
                
                {/* Claim Date and Claim Value */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Claim Date</label>
                    <DateInput
                      value={formData.claimDate}
                      onChange={(date) => {
                        setFormData(prev => ({ ...prev, claimDate: date }));
                        setErrors(prev => ({ ...prev, claimDate: '' }));
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Claim Value</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <span className="text-gray-500 font-medium">R</span>
                      </div>
                      <input
                        type="text"
                        name="claimValue"
                        value={formData.claimValue}
                        onChange={handleInputChange}
                        onBlur={handleClaimValueBlur}
                        className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-4 border-t">

                  <button
                    type="button"
                    onClick={() => {
                      if (isMasterQuoteMode && returnTo) {
                        // Go back to master quote page with refresh flag
                        const decodedReturnUrl = decodeURIComponent(returnTo);
                        const separator = decodedReturnUrl.includes('?') ? '&' : '?';
                        const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=previous_claim&clientId=${clientId}`;
                        console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
                        navigate(finalUrl);
                      } else {
                        navigate(`/create-quote/${clientId}`);
                      }
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md disabled:bg-gray-400"
                  >
                    {isLoading ? 'Saving...' : (editingId ? 'Update Claim' : 'Add Claim')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPreviousClaimPage;